import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../../utils/apiConfig';

// Async thunk for fetching user wallet
export const fetchUserWallet = createAsyncThunk(
  'wallet/fetchUserWallet',
  async (_, { rejectWithValue, getState }) => {
    // Check if we already have data and it's not stale
    const state = getState();
    if (state.wallet.wallets && 
        state.wallet.availableBalance && 
        !state.wallet.error &&
        state.wallet.lastFetched &&
        (Date.now() - state.wallet.lastFetched) < 5 * 60 * 1000) { // 5 minutes cache
      // Return existing data without making API call
      return {
        wallets: state.wallet.wallets,
        availableBalance: state.wallet.availableBalance,
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/user-wallet`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        return {
          wallets: data.data.wallets,
          availableBalance: data.data,
        };
      } else {
        return {
          wallets: data.wallets || [],
          availableBalance: data.data || null,
        };
      }
    } catch (err) {
      console.error('Error fetching user wallet:', err);
      return rejectWithValue(err.message || 'Failed to fetch wallet data');
    }
  }
);

// Initial state
const initialState = {
  wallets: null,
  availableBalance: null,
  loading: false,
  error: null,
  lastFetched: null,
};

// Wallet slice
const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    clearWallet: (state) => {
      state.wallets = null;
      state.availableBalance = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user wallet
      .addCase(fetchUserWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserWallet.fulfilled, (state, action) => {
        state.loading = false;
        state.wallets = action.payload.wallets;
        state.availableBalance = action.payload.availableBalance;
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchUserWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch wallet data';
      });
  },
});

// Export actions
export const { clearWallet } = walletSlice.actions;

// Export selectors
export const selectWallets = (state) => state.wallet.wallets;
export const selectAvailableBalance = (state) => state.wallet.availableBalance;
export const selectWalletLoading = (state) => state.wallet.loading;
export const selectWalletError = (state) => state.wallet.error;
export const selectLastFetched = (state) => state.wallet.lastFetched;

// Export reducer
export default walletSlice.reducer;

